import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { IStartEndDateRange } from '@app/eds';
import { DefaultDateRange } from '@app/eds/constants';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-daterange',
  templateUrl: './daterange.component.html',
  styleUrls: ['./daterange.component.scss']
})
export class DaterangeComponent implements OnInit, OnDestroy {
  range = new FormGroup({
    //start date for date range
    start: new FormControl(new Date()),
    //end date for date range
    end: new FormControl(new Date())
  });

  // custom error
  customError = true;

  // Subscription for whenever the start date is changed
  startDateSubscription!: Subscription;

  // An Input contains the custom class style of calendar panel
  @Input() rangePickerPanelClass!: string | string[];

  // An Input contains the custom classes Style of footer panel
  @Input() actionsFooterClasses!: string | string[];

  // Emits when currently selected date changes.
  @Output() selectedStartDate: EventEmitter<IStartEndDateRange> =
    new EventEmitter<IStartEndDateRange>();

  // Emits when currently selected date changes.
  @Output() selectedEndDate: EventEmitter<IStartEndDateRange> =
    new EventEmitter<IStartEndDateRange>();

  // date range for calendar
  @Input() dateRange: number = DefaultDateRange.DATE_RANGE;

  constructor(
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly translate: TranslateService
  ) {
    this.dateAdapter.setLocale(this.translate.instant('datepicker.languageCode'));
  }

  ngOnInit() {
    this.startDateSubscription = this.range.controls.start.valueChanges.subscribe(
      (value) => {
        if (
          this.range.controls.end.value === null &&
          this.range.controls.start.value !== null
        ) {
          this.setEndDate(value);
        }
      }
    );
  }

  ngOnDestroy() {
    this.startDateSubscription.unsubscribe();
  }

  /**
   * Function emits start date
   *
   * @param {IStartEndDateRange} startEndDate
   */
  dateSelectedStart = (startEndDate: IStartEndDateRange) =>
    this.selectedStartDate.emit(startEndDate);

  /**
   * Function emits end date
   *
   * @param {IStartEndDateRange} startEndDate
   */
  dateSelectedEnd = (startEndDate: IStartEndDateRange) => {
    if (startEndDate.end === null) {
      this.setEndDate(startEndDate.start);
    }
    this.selectedEndDate.emit(startEndDate);
  };

  /**
   * This function will trigger the reset of form
   */
  resetDateRange = () => this.range.reset();

  /**
   * Sets the end date whenever a single date selection
   * is made from calendar then the same date has to be
   * set for the end date as well.
   *
   * @param {Date | null | undefined} date
   */
  setEndDate = (date: Date | null | undefined) => this.range.patchValue({ end: date });
}
